import React, { useState, useEffect } from 'react';
import { automunityAPI } from '../lib/api';
import './Dashboard.css';

interface StatTile {
  title: string;
  subtitle: string;
  value: number;
  icon: string;
  color: string;
}

interface TeamCount {
  name: string;
  count: number;
  color: string;
}

interface AutomationLeader {
  name: string;
  timeSavedHours: number;
}

interface ContributionLeader {
  name: string;
  count: number;
}

const TEAM_COLORS = [
  '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#84cc16',
  '#a855f7', '#0ea5e9', '#e11d48', '#22c55e', '#eab308'
];

const Dashboard: React.FC = () => {
  const [statTiles, setStatTiles] = useState<StatTile[]>([
    { title: 'Automations', subtitle: '# of automations', value: 0, icon: '⚙️', color: '#6366f1' },
    { title: 'Time Saved', subtitle: 'In Hours', value: 0, icon: '⏱️', color: '#10b981' },
    { title: 'Money Saved', subtitle: '$$$', value: 0, icon: '💰', color: '#f59e0b' },
    { title: 'Contributions', subtitle: '', value: 0, icon: '👥', color: '#ef4444' }
  ]);
  const [teamCounts, setTeamCounts] = useState<TeamCount[]>([]);
  const [topAutomations, setTopAutomations] = useState<AutomationLeader[]>([]);
  const [topContributors, setTopContributors] = useState<ContributionLeader[]>([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        let automationList: any[] = [];
        let contributionList: any[] = [];
        let valueObj: any = {};

        // Fetch each independently so one failure doesn't block all data
        try {
          const automationsRes = await automunityAPI.getAutomations();
          const automations = await automationsRes.body.json();
          automationList = Array.isArray(automations) ? automations 
            : Array.isArray((automations as any)?.records) ? (automations as any).records 
            : typeof (automations as any)?.records === 'string' ? JSON.parse((automations as any).records) : [];
        } catch (err) {
          console.error('Dashboard: Failed to fetch automations:', err);
        }

        try {
          const contributionsRes = await automunityAPI.getContributions();
          const contributions = await contributionsRes.body.json();
          contributionList = Array.isArray(contributions) ? contributions
            : Array.isArray((contributions as any)?.records) ? (contributions as any).records : [];
        } catch (err) {
          console.error('Dashboard: Failed to fetch contributions:', err);
        }

        try {
          const valueRes = await automunityAPI.getMonitorValue();
          const valueData = await valueRes.body.json();
          const valueParsed = typeof valueData === 'string' ? JSON.parse(valueData) : valueData;
          valueObj = valueParsed?.records ? (typeof valueParsed.records === 'string' ? JSON.parse(valueParsed.records) : valueParsed.records) : valueParsed;
        } catch (err) {
          console.error('Dashboard: Failed to fetch value metrics:', err);
        }

        const automationCount = automationList.length;

        // Fallback: if the contributions endpoint returned nothing, extract from
        // the `contributors` JSON field embedded in each automation (from v_automation view)
        if (contributionList.length === 0) {
          automationList.forEach((auto: any) => {
            let contribs = auto.contributors;
            if (typeof contribs === 'string') {
              try { contribs = JSON.parse(contribs); } catch { contribs = null; }
            }
            if (Array.isArray(contribs)) {
              contributionList = contributionList.concat(contribs);
            }
          });
        }

        const contributionCount = contributionList.length;
        
        // Get time and money saved from the monitor/value endpoint (per-invocation automations)
        const totals = valueObj?.totals || {};
        const perInvocationMoney = totals.total_money_saved || 0;
        const totalTimeSaved = totals.total_time_saved_hours || 0;

        // Add fixed-recurring value from automations in Production with monthly_value_saved
        let fixedRecurringMoney = 0;
        automationList.forEach((auto: any) => {
          const category = auto.valuation_category;
          const monthlyVal = parseFloat(auto.monthly_value_saved) || 0;
          if (['2', '3', '4', '5'].includes(String(category)) && monthlyVal > 0) {
            let stages = auto.stages;
            if (typeof stages === 'string') { try { stages = JSON.parse(stages); } catch { stages = []; } }
            const inProduction = Array.isArray(stages) && stages.some((s: any) => s.name === 'Production' || s.name === 'Maintenance');
            if (inProduction) fixedRecurringMoney += monthlyVal;
          }
        });

        const totalMoneySaved = perInvocationMoney + fixedRecurringMoney;

        // Build leaderboard: automations ranked by time saved (from value endpoint)
        const valueAutomations = valueObj?.automations || [];
        const automationLeaders: AutomationLeader[] = valueAutomations
          .filter((a: any) => a.time_saved_hours > 0)
          .map((a: any) => ({ name: a.automation_name || a.name, timeSavedHours: a.time_saved_hours }))
          .sort((a: AutomationLeader, b: AutomationLeader) => b.timeSavedHours - a.timeSavedHours)
          .slice(0, 10);
        setTopAutomations(automationLeaders);

        // Build leaderboard: users ranked by contribution count (exclude developers)
        const contributorMap = new Map<string, number>();
        contributionList.forEach((c: any) => {
          if (c.is_developer === 1 || c.is_developer === true || c.is_developer === '1') return;
          const name = c.full_name || c.user_name || `User ${c.user_id}`;
          if (!name || name === 'Unknown') return;
          contributorMap.set(name, (contributorMap.get(name) || 0) + 1);
        });
        const contributionLeaders: ContributionLeader[] = Array.from(contributorMap.entries())
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 10);
        setTopContributors(contributionLeaders);
        
        // Group by team for pie chart
        // Count each automation under ALL its teams (reflects team involvement, not unique count)
        const teamMap = new Map<string, number>();
        automationList.forEach((auto: any) => {
          const teamNames = auto.team_names
            ? auto.team_names.split(',').map((t: string) => t.trim()).filter(Boolean)
            : auto.team_name ? [auto.team_name] : [];
          if (teamNames.length === 0) {
            teamMap.set('Unassigned', (teamMap.get('Unassigned') || 0) + 1);
          } else {
            teamNames.forEach((name: string) => {
              teamMap.set(name, (teamMap.get(name) || 0) + 1);
            });
          }
        });
        
        const teamData: TeamCount[] = Array.from(teamMap.entries())
          .map(([name, count], i) => ({ name, count, color: TEAM_COLORS[i % TEAM_COLORS.length] }))
          .sort((a, b) => b.count - a.count);
        setTeamCounts(teamData);

        setStatTiles([
          { title: 'Automations', subtitle: '# of automations', value: automationCount, icon: '⚙️', color: '#6366f1' },
          { title: 'Time Saved', subtitle: 'In Hours', value: Math.round(totalTimeSaved), icon: '⏱️', color: '#10b981' },
          { title: 'Money Saved', subtitle: '$$$', value: Math.round(totalMoneySaved), icon: '💰', color: '#f59e0b' },
          { title: 'Contributions', subtitle: '', value: contributionCount, icon: '👥', color: '#ef4444' }
        ]);
      } catch (error) {
        console.error('Dashboard: Unexpected error:', error);
      }
    };

    fetchDashboardData();
  }, []);

  const handleViewAutomations = () => {
    window.location.href = '/automations';
  };

  const handleCreateAutomation = () => {
    window.location.href = '/automations/create';
  };

  const handleLearnAboutAutomation = () => {
    window.location.href = '/chat';
  };

  return (
    <div className="dashboard">
      <div className="welcome-banner">
        <div className="banner-content">
          <h1>Welcome back to the <strong>Automunity</strong> Dashboard!</h1>
          <p>Building A Community of Automation Champions</p>
        </div>
        <div className="banner-decoration">🚀</div>
      </div>

      <div className="action-buttons">
        <button className="btn btn-primary" onClick={handleViewAutomations}>
          <span className="btn-icon">👀</span>
          Let's see what automations we have!
        </button>
        <button className="btn btn-success" onClick={handleCreateAutomation}>
          <span className="btn-icon">✨</span>
          I'm ready to create an automation!
        </button>
        <button className="btn btn-secondary" onClick={handleLearnAboutAutomation}>
          <span className="btn-icon">🤔</span>
          I'm not quite sure what an automation is.
        </button>
      </div>

      <div className="stat-tiles">
        {statTiles.map((tile, index) => (
          <div key={index} className="stat-tile" style={{'--tile-color': tile.color} as React.CSSProperties}>
            <div className="stat-header">
              <span className="stat-icon">{tile.icon}</span>
              <h4>{tile.title}</h4>
            </div>
            <div className="stat-content">
              <div className="stat-value">{tile.value.toLocaleString()}</div>
              <div className="stat-subtitle">{tile.subtitle}</div>
            </div>
          </div>
        ))}
      </div>

      {teamCounts.length > 0 && (
        <div className="dashboard-charts">
          <div className="chart-card">
            <h3>Automations by Team</h3>
            <p className="chart-subtitle" style={{ fontSize: '12px', color: '#64748b', marginTop: '-4px', marginBottom: '8px' }}>
              Some automations appear under multiple teams
            </p>
            <div className="pie-chart-container">
              <svg viewBox="0 0 600 600" className="pie-chart">
                {(() => {
                  const total = teamCounts.reduce((sum, t) => sum + t.count, 0);
                  const cx = 300, cy = 300, radius = 140;
                  let cumulative = 0;

                  // Pre-calculate label positions and resolve overlaps
                  const labels = teamCounts.map((team, i) => {
                    const percentage = team.count / total;
                    const startAngle = cumulative * 360;
                    cumulative += percentage;
                    const endAngle = cumulative * 360;
                    const midAngle = (startAngle + endAngle) / 2;
                    const midRad = (midAngle - 90) * Math.PI / 180;
                    const isRight = midAngle <= 180;
                    const lineEndRadius = 220;
                    const lineEndX = cx + lineEndRadius * Math.cos(midRad);
                    const lineEndY = cy + lineEndRadius * Math.sin(midRad);
                    return { team, percentage, startAngle, endAngle, midAngle, midRad, isRight, lineEndX, lineEndY };
                  });

                  // Resolve vertical overlap: sort labels by Y position per side and space them apart
                  const minGap = 18;
                  const leftLabels = labels.filter(l => !l.isRight).sort((a, b) => a.lineEndY - b.lineEndY);
                  const rightLabels = labels.filter(l => l.isRight).sort((a, b) => a.lineEndY - b.lineEndY);

                  [leftLabels, rightLabels].forEach(side => {
                    for (let i = 1; i < side.length; i++) {
                      const prev = side[i - 1];
                      const curr = side[i];
                      if (curr.lineEndY - prev.lineEndY < minGap) {
                        curr.lineEndY = prev.lineEndY + minGap;
                      }
                    }
                  });

                  return labels.map((l, i) => {
                    const { team, percentage, startAngle, endAngle, midRad, isRight, lineEndY } = l;
                    const largeArc = percentage > 0.5 ? 1 : 0;
                    const startRad = (startAngle - 90) * Math.PI / 180;
                    const endRad = (endAngle - 90) * Math.PI / 180;
                    const x1 = cx + radius * Math.cos(startRad);
                    const y1 = cy + radius * Math.sin(startRad);
                    const x2 = cx + radius * Math.cos(endRad);
                    const y2 = cy + radius * Math.sin(endRad);

                    // Leader line
                    const lineStartX = cx + (radius + 5) * Math.cos(midRad);
                    const lineStartY = cy + (radius + 5) * Math.sin(midRad);
                    const elbowX = isRight ? 530 : 70;
                    const textAnchor = isRight ? 'start' : 'end';
                    const textX = isRight ? elbowX + 6 : elbowX - 6;

                    if (teamCounts.length === 1) {
                      return (
                        <g key={i}>
                          <circle cx={cx} cy={cy} r={radius} fill={team.color} />
                          <text x={cx} y={cy + 5} textAnchor="middle" fontSize="13" fontWeight="600" fill="#fff">
                            {team.name} ({team.count})
                          </text>
                        </g>
                      );
                    }
                    return (
                      <g key={i}>
                        <path
                          d={`M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`}
                          fill={team.color}
                          stroke="#fff"
                          strokeWidth="1"
                        >
                          <title>{team.name}: {team.count} ({(percentage * 100).toFixed(1)}%)</title>
                        </path>
                        <polyline
                          points={`${lineStartX},${lineStartY} ${elbowX},${lineEndY}`}
                          fill="none"
                          stroke={team.color}
                          strokeWidth="1.2"
                          opacity="0.7"
                        />
                        <circle cx={elbowX} cy={lineEndY} r="2.5" fill={team.color} />
                        <text x={textX} y={lineEndY + 4} textAnchor={textAnchor} fontSize="11" fontWeight="500" fill="#334155">
                          {team.name} ({team.count})
                        </text>
                      </g>
                    );
                  });
                })()}
              </svg>
            </div>
          </div>
        </div>
      )}

      <div className="leaderboards">
        <div className="leaderboard-card">
          <h3>🏆 Top Automations by Time Saved</h3>
          {topAutomations.length > 0 ? (
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Automation</th>
                  <th>Hours Saved</th>
                </tr>
              </thead>
              <tbody>
                {topAutomations.map((auto, i) => (
                  <tr key={i}>
                    <td className="rank-cell">{i + 1}</td>
                    <td>{auto.name}</td>
                    <td className="value-cell">{auto.timeSavedHours.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="leaderboard-empty">
              <span className="empty-icon">📊</span>
              <p>Insufficient data — start adding automation financials (time gain per objective &amp; invocations) to see which automations save the most time!</p>
            </div>
          )}
        </div>

        <div className="leaderboard-card">
          <h3>🌟 Top Contributors</h3>
          {topContributors.length > 0 ? (
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Contributor</th>
                  <th>Contributions</th>
                </tr>
              </thead>
              <tbody>
                {topContributors.map((user, i) => (
                  <tr key={i}>
                    <td className="rank-cell">{i + 1}</td>
                    <td>{user.name}</td>
                    <td className="value-cell">{user.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="leaderboard-empty">
              <span className="empty-icon">👥</span>
              <p>Insufficient data — start adding contributors to automations to see who's making the biggest impact!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;